import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
} from '@mui/material';
import { Add, Edit, Delete, WidgetsOutlined } from '@mui/icons-material';
import { useGetCompositeComponentsQuery } from '@/state/queries/composite-components/use-get-composite-components';
import { useCreateCompositeComponentMutation } from '@/state/mutations/composite-components/use-create-composite-component';
import { useUpdateCompositeComponentMutation } from '@/state/mutations/composite-components/use-update-composite-component';
import { useDeleteCompositeComponentMutation } from '@/state/mutations/composite-components/use-delete-composite-component';
import OrganizationContentStack from '@/components/organization-content-stack';
import PageHeaderBar from '@/components/common/page-header-bar';
import EmptyState from '@/components/common/empty-state';
import CompositeComponentFormModal from './composite-component-form.modal';
import type { CompositeComponentDto } from '@/state/queries/composite-components/use-get-composite-components';
import type { CreateCompositeComponentDto } from '@/state/mutations/composite-components/use-create-composite-component';

export default function CompositeComponentsPage() {
  const { data: components, isLoading, error } = useGetCompositeComponentsQuery();
  const createMutation = useCreateCompositeComponentMutation();
  const updateMutation = useUpdateCompositeComponentMutation();
  const deleteMutation = useDeleteCompositeComponentMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CompositeComponentDto | null>(null);

  const handleOpenCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (c: CompositeComponentDto) => {
    setEditing(c);
    setModalOpen(true);
  };

  const handleSave = async (data: CreateCompositeComponentDto, componentId?: string) => {
    if (componentId) {
      await updateMutation.mutateAsync({
        id: componentId,
        data: {
          name: data.name,
          definition: data.definition,
          role: data.role,
          allowedIn: data.allowedIn,
          applicability: data.applicability,
          sortOrderByScope: data.sortOrderByScope,
        },
      });
    } else {
      await createMutation.mutateAsync(data);
    }
    setModalOpen(false);
    setEditing(null);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this composite component?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <OrganizationContentStack
      content={
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <PageHeaderBar
            mainContent={
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <WidgetsOutlined sx={{ fontSize: 24 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Composite Components
                  </Typography>
                </Box>
                <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate} sx={{ flexShrink: 0 }}>
                  Create
                </Button>
              </Box>
            }
          />

          <Card sx={{ mt: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <CardContent
              sx={{
                'flex': 1,
                'minHeight': 0,
                'display': 'flex',
                'flexDirection': 'column',
                'overflow': 'hidden',
                'p': 0,
                '&:last-child': { pb: 0 },
              }}
            >
              {isLoading ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography>Loading...</Typography>
                </Box>
              ) : error ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="error">Error loading components.</Typography>
                </Box>
              ) : !components || components.length === 0 ? (
                <Box sx={{ p: 4 }}>
                  <EmptyState
                    text="No composite components yet"
                    icon={<WidgetsOutlined sx={{ fontSize: 48, color: 'text.secondary' }} />}
                  />
                </Box>
              ) : (
                <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Slug</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Components</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {components.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell>
                            <Typography variant="body2">{c.name}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {c.slug}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {c.role === 'infoOverlay' ? 'Info overlay' : 'Virtual media'}
                            </Typography>
                          </TableCell>
                          <TableCell>{c.definition?.components?.length ?? 0} component(s)</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5}>
                              <IconButton size="small" onClick={() => handleOpenEdit(c)} aria-label="Edit">
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(c.id)}
                                aria-label="Delete"
                                color="error"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          <CompositeComponentFormModal
            open={modalOpen}
            onClose={() => {
              setModalOpen(false);
              setEditing(null);
            }}
            component={editing}
            onSave={handleSave}
            isSaving={isSaving}
          />
        </Box>
      }
    />
  );
}
